<template>
  <!-- Same edit chrome as PrinterForm / ProductForm / TableManagementForm (settings-top-bar + card + loader). -->
  <form @submit.prevent="onSubmit">
    <div class="integration-form-shell">
      <div :class="(showFormLoader ? 'show-loader' : 'hide-loader')">
        <div class="el-loading-spinner">
          <svg viewBox="25 25 50 50" class="circular">
            <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
          </svg>
        </div>
      </div>

    <div class="settings-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="$emit('back')">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
        <p class="d-inline-flex">
          <a
            v-if="showDeleteButton"
            class="btn delete mar-rt-10"
            :class="{ disabled: busy || disabled || actionBusy }"
            :title="$t('general.delete')"
            @click.prevent="onDeleteClick"
          >
            <img src="/static/img/icons/icon-close.png" alt="">
            {{ $t('general.delete') }}
          </a>
          <button
            v-if="showValidateButton"
            type="button"
            class="btn btn-info btn-icon mar-rt-10 validate"
            :disabled="validateDisabled || actionBusy"
            :title="$t('general.validate')"
            @click.prevent="startValidationFlow"
          >
            {{ $t('general.validate') }}
          </button>
          <!-- Backup: automatic marketplace activation is intentionally disabled.
          <button
            v-if="showActivateButton"
            type="button"
            class="btn btn-info btn-icon mar-rt-10 activate"
            :disabled="activateDisabled || actionBusy"
            :title="$t('general.activate')"
            @click.prevent="startActivationFlow"
          >
            {{ $t('general.activate') }}
          </button>
          -->
          <button
            type="submit"
            class="btn btn-success btn-icon mar-rt-10 save"
            :disabled="saveDisabled || actionBusy"
            :title="saveButtonTitle"
          >
            <img src="/static/img/icons/save.png">
            {{ saveButtonLabel }}
          </button>
        </p>
      </div>
    </div>

    <div class="row mar-to-15 mar-bt-15 integration-form-layout">
      <div class="col-lg-12 col-md-12">
        <div class="card integration-form-layout__card">
          <div class="card-content">
            <h5 v-if="heading" class="mar-bt-15">{{ heading }}</h5>
            <wolt-integration-form
              v-if="isWoltIntegration"
              :form-data="formData"
              :locations="locations"
              :top-categories="topCategories"
              :disabled="disabled"
              :field-label-fn="fieldLabelFn"
              :required-fields="woltRequiredFields"
              :validation-errors="woltValidationErrors"
              :show-field-validation="showFieldValidation"
            />
            <wolt-drive-integration-form
              v-else-if="isWoltDriveIntegration"
              :form-data="formData"
              :locations="locations"
              :disabled="disabled"
              :occupied-location-ids="occupiedLocationIds"
              :is-create-mode="isCreateMode"
              :field-label-fn="fieldLabelFn"
              :required-fields="woltDriveRequiredFields"
              :validation-errors="woltDriveValidationErrors"
              :show-field-validation="showFieldValidation"
            />
            <delivery-hero-integration-form
              v-else-if="isDeliveryHeroIntegration"
              :form-data="formData"
              :locations="locations"
              :disabled="disabled"
              :occupied-location-ids="occupiedLocationIds"
              :is-create-mode="isCreateMode"
              :field-label-fn="fieldLabelFn"
              :required-fields="deliveryHeroRequiredFields"
              :credential-fields="deliveryHeroCredentialFields"
              :validation-errors="deliveryHeroValidationErrors"
              :show-field-validation="showFieldValidation"
              :regenerating-keys="regeneratingDeliveryHeroKeys"
              @regenerate-keys="$emit('regenerate-keys')"
            />
            <bolt-integration-form
              v-else-if="isBoltIntegration"
              :form-data="formData"
              :locations="locations"
              :top-categories="topCategories"
              :disabled="disabled"
              :field-label-fn="fieldLabelFn"
              :required-fields="boltRequiredFields"
              :validation-errors="boltValidationErrors"
              :show-field-validation="showFieldValidation"
            />
            <glovo-laas-integration-form
              v-else-if="isGlovoLaasIntegration"
              :form-data="formData"
              :locations="locations"
              :disabled="disabled"
              :occupied-location-ids="occupiedLocationIds"
              :is-create-mode="isCreateMode"
              :field-label-fn="fieldLabelFn"
              :required-fields="glovoLaasRequiredFields"
              :validation-errors="glovoLaasValidationErrors"
              :show-field-validation="showFieldValidation"
            />
            <glovo-integration-form
              v-else-if="isGlovoIntegration"
              :form-data="formData"
              :locations="locations"
              :top-categories="topCategories"
              :disabled="disabled"
              :field-label-fn="fieldLabelFn"
              :required-fields="glovoRequiredFields"
              :validation-errors="glovoValidationErrors"
              :show-field-validation="showFieldValidation"
            />
            <pricelist-fields-form
              v-else-if="fieldNames && fieldNames.length"
              :field-names="fieldNames"
              :form-data="formData"
              :form-text="formText"
              :disabled="disabled"
              :boolean-fields="booleanFields"
              :json-fields="jsonFields"
              :json-hint="jsonHint"
              :field-label-fn="fieldLabelFn"
              :read-only-fields="readOnlyFields"
            />

            <div v-if="showWebhookStatus" class="integration-webhook-status">
              <hr class="sm">
              <div class="integration-webhook-status__row">
                <span class="integration-webhook-status__label">{{ $t('gestiune.integrationsWebhookStatusLabel') }}</span>
                <span
                  class="integration-webhook-status__badge"
                  :class="webhookRegistered ? 'integration-webhook-status__badge--ok' : 'integration-webhook-status__badge--warn'"
                >
                  {{ webhookRegistered ? $t('gestiune.integrationsWebhookRegistered') : $t('gestiune.integrationsWebhookNotRegistered') }}
                </span>
              </div>
              <p v-if="!webhookRegistered" class="integration-webhook-status__hint">
                {{ $t('gestiune.integrationsWebhookRegisterHint') }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="isMarketplaceIntegration" class="card integration-form-layout__card integration-form-actions-card">
          <div class="card-content">
            <h5>{{ $t('marketplace.operations') }}</h5>
            <div class="marketplace-action-grid">
              <section class="marketplace-action-panel marketplace-action-panel--menu">
                <div class="marketplace-action-heading"><i class="ti-menu-alt" aria-hidden="true"></i><div><h6>{{ $t('marketplace.menuActions') }}</h6><p>{{ $t('marketplace.menuActionsHint') }}</p></div></div>
                <div class="row marketplace-menu-settings">
                  <integration-menu-top-categories-field :form-data="formData" :top-categories="topCategories" :disabled="disabled || busy || actionBusy" :field-label-fn="fieldLabelFn" :show-filter-note="!isGlovoIntegration" />
                  <div v-if="isBoltIntegration" class="col-md-6 form-group marketplace-menu-settings__language"><label for="bolt-menu-language">{{ fieldLabelFn ? fieldLabelFn('menu_language') : 'Menu language' }}</label><select id="bolt-menu-language" v-model="formData.menu_language" class="form-control" :disabled="disabled || busy || actionBusy"><option value="ro-RO">RO</option><option value="en-US">EN</option></select></div>
                </div>
                <p v-if="!showIntegrationActions" class="marketplace-field-hint">{{ $t('marketplace.saveBeforeActions') }}</p>
                <div v-if="isGlovoIntegration" class="integration-glovo-menu">
              <div class="integration-glovo-menu__summary">
                <div class="integration-glovo-menu__summary-row">
                  <span class="integration-glovo-menu__summary-label">{{ $t('gestiune.integrationsGlovoExportsToday') }}</span>
                  <span>{{ glovoMenuExportsToday }}</span>
                </div>
                <div class="integration-glovo-menu__summary-row">
                  <span class="integration-glovo-menu__summary-label">{{ $t('gestiune.integrationsGlovoLastExport') }}</span>
                  <span>{{ glovoLastExportLabel }}</span>
                  <span
                    v-if="glovoExportStatusBadge"
                    class="integration-glovo-menu__badge"
                    :class="'integration-glovo-menu__badge--' + glovoExportStatusBadge.type"
                  >
                    {{ glovoExportStatusBadge.label }}
                  </span>
                </div>
                <div v-if="glovoLastExportStatus" class="integration-glovo-menu__summary-row">
                  <span class="integration-glovo-menu__summary-label">{{ $t('gestiune.integrationsGlovoExportStatus') }}</span>
                  <span>{{ glovoLastExportStatus }}</span>
                </div>
                <div v-if="glovoLastExportDetails.length" class="integration-glovo-menu__details">
                  <p
                    v-for="(detail, detailIdx) in glovoLastExportDetails"
                    :key="'glovo-export-detail-' + detailIdx"
                    class="integration-glovo-menu__detail-line"
                  >
                    {{ detail }}
                  </p>
                </div>
              </div>

              <div class="integration-glovo-menu__buttons">
                <button
                  type="button"
                  class="btn btn-info"
                  :disabled="disabled || busy || actionBusy || !showIntegrationActions"
                  @click.prevent="openMenuPreview"
                >
                  {{ $t('gestiune.integrationsMenuPreviewOpen') }}
                </button>
                <!-- Standalone menu validation/upload is available through Preview Menu.
                <button
                  type="button"
                  class="btn add"
                  :disabled="disabled || busy || actionBusy || !showIntegrationActions"
                  @click.prevent="validateAndPushMenu()"
                >
                  {{ $t('gestiune.integrationsValidateAndPushMenu') }}
                </button>
                -->
                <button
                  type="button"
                  class="btn btn-info"
                  :disabled="disabled || busy || actionBusy || !canCheckGlovoExportStatus || !showIntegrationActions"
                  :title="canCheckGlovoExportStatus ? '' : $t('gestiune.integrationsGlovoCheckStatusDisabledHint')"
                  @click.prevent="checkGlovoExportStatus"
                >
                  {{ $t('gestiune.integrationsGlovoCheckStatus') }}
                </button>
              </div>
                </div>
                <button v-else type="button" class="btn btn-info marketplace-primary-action" :disabled="disabled || busy || actionBusy || !showIntegrationActions" @click.prevent="openMenuPreview">
                  <i class="ti-eye" aria-hidden="true"></i> {{ $t('gestiune.integrationsMenuPreviewOpen') }}
                </button>
                <div class="marketplace-import-action"><platform-menu-import-link :location-id="formData.location_id" /></div>
                <div class="marketplace-action-notice">
                  <i class="fa fa-info-circle" aria-hidden="true"></i><div><p>{{ $t('gestiune.integrationsMenuUploadCampaignsHint') }}</p><p v-if="isGlovoIntegration">{{ $t('gestiune.integrationsGlovoMenuSyncDailyLimitHint') }}</p></div>
                </div>
              </section>
              <div class="marketplace-action-stack">
                <section v-if="isWoltIntegration && showIntegrationActions" class="marketplace-action-panel">
                  <div class="marketplace-action-heading"><i class="ti-link" aria-hidden="true"></i><div><h6>{{ $t('marketplace.connection') }}</h6><p>{{ $t('marketplace.connectionHint') }}</p></div></div>
                  <button type="button" class="btn btn-info" :disabled="disabled || busy || actionBusy || !formData.venue_id || !formData.client_id || !formData.client_secret" @click.prevent="enrollWolt">{{ $t(formData.is_integrated ? 'wolt.updateCredentials' : 'wolt.getWolt') }}</button>
                </section>
                <section class="marketplace-action-panel">
                  <div class="marketplace-action-heading"><i class="ti-shopping-cart" aria-hidden="true"></i><div><h6>{{ $t('marketplace.orderSettings') }}</h6><p>{{ $t('marketplace.orderSettingsHint') }}</p></div></div>
                  <marketplace-order-settings :form-data="formData" :platform="integrationType" :disabled="disabled || busy || actionBusy" :field-label-fn="fieldLabelFn" />
                  <div v-if="showIntegrationActions" class="marketplace-action-line"><label>{{ $t('bolt.acceptOrders') }}</label><p-switch v-model="acceptOrdersStatus" type="success" name="integration-accept-orders" :disabled="disabled || busy || actionBusy" @input="handleAcceptOrdersStatus" /></div>

                </section>
              </div>
            </div>
            <section v-if="isWoltIntegration || isBoltIntegration" class="marketplace-action-panel marketplace-schedule-panel">
              <div class="marketplace-schedule-panel__header">
                <div class="marketplace-action-heading"><i class="ti-time" aria-hidden="true"></i><div><h6>{{ $t('marketplace.scheduleTitle') }}</h6><p>{{ $t('marketplace.scheduleHint') }}</p></div></div>
                <button type="button" class="btn btn-info" :disabled="disabled || busy || actionBusy || !showScheduleAction" @click.prevent="syncIntegrationSchedule"><i class="ti-reload" aria-hidden="true"></i> {{ $t('gestiune.integrationsSyncSchedule') }}</button>
              </div>
              <integration-schedule-editor :form-data="formData" :platform="integrationType" :disabled="disabled || busy || actionBusy" />
              <p v-if="!showIntegrationActions" class="marketplace-field-hint">{{ $t('marketplace.saveBeforeActions') }}</p>
            </section>
            <section v-if="isBoltIntegration" class="marketplace-action-panel marketplace-notifications-panel">
              <div class="marketplace-action-heading"><i class="ti-email" aria-hidden="true"></i><div><h6>{{ $t('marketplace.notifications') }}</h6><p>{{ $t('gestiune.integrationsFeedbackEmailsHint') }}</p></div></div>
              <label for="bolt-feedback-emails">{{ fieldLabelFn ? fieldLabelFn('emails') : 'Emails' }}</label>
              <textarea id="bolt-feedback-emails" v-model="formData.emails" class="form-control" rows="2" :disabled="disabled || busy || actionBusy" :placeholder="$t('gestiune.integrationsFeedbackEmailsHint')"></textarea>
              <span v-if="boltValidationErrors.emails" class="text-danger help-block">{{ boltValidationErrors.emails }}</span>
            </section>
          </div>
        </div>

      </div>
    </div>
    </div>
    <integration-menu-preview-modal
      :visible="menuPreviewVisible"
      :busy="menuPreviewBusy"
      :importing="menuPreviewImporting"
      :layout="menuPreviewLayout"
      :currency="menuPreviewCurrency"
      :categories="menuPreviewCategories"
      :error-message="menuPreviewError"
      :warnings="menuPreviewWarnings"
      :title="menuPreviewTitle"
      :subtitle="menuPreviewSubtitle"
      @close="closeMenuPreview"
      @import="confirmImportMenuFromPreview"
    />
  </form>
</template>

<script>
import PricelistFieldsForm from 'src/components/Dashboard/Views/Management/Pricelist/PricelistFieldsForm.vue'
import WoltIntegrationForm from 'src/components/Dashboard/Views/Integrations/WoltIntegrationForm.vue'
import WoltDriveIntegrationForm from 'src/components/Dashboard/Views/Integrations/WoltDriveIntegrationForm.vue'
import BoltIntegrationForm from 'src/components/Dashboard/Views/Integrations/BoltIntegrationForm.vue'
import DeliveryHeroIntegrationForm from 'src/components/Dashboard/Views/Integrations/DeliveryHeroIntegrationForm.vue'
import GlovoLaasIntegrationForm from 'src/components/Dashboard/Views/Integrations/GlovoLaasIntegrationForm.vue'
import GlovoIntegrationForm from 'src/components/Dashboard/Views/Integrations/GlovoIntegrationForm.vue'
import IntegrationMenuPreviewModal from 'src/components/Dashboard/Views/Integrations/MenuPreview/IntegrationMenuPreviewModal.vue'
import PlatformMenuImportLink from 'src/components/Dashboard/Views/Integrations/PlatformMenuImports/PlatformMenuImportLink.vue'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import IntegrationMenuTopCategoriesField from './IntegrationMenuTopCategoriesField.vue'
import IntegrationScheduleEditor from './IntegrationScheduleEditor.vue'
import MarketplaceOrderSettings from './MarketplaceOrderSettings.vue'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'
import Vue from 'vue'
import IntegrationMenuValidationCollapse from 'src/components/Dashboard/Views/Integrations/IntegrationMenuValidationCollapse.vue'
import {
  extractMenuValidationGroups,
  extractApiResponseMessage,
  getMenuValidationSummary,
  getMenuValidationEntityEditPath,
  isMenuValidationSuccessful,
  canProceedWithMenuValidation,
  isMenuNotFoundResponse,
  getIntegrationActionErrorMessage as resolveIntegrationActionErrorMessage
} from 'src/components/Dashboard/Views/Integrations/integrationMenuValidationHelpers.js'

export default {
  name: 'IntegrationForm',
  components: {
    PricelistFieldsForm,
    WoltIntegrationForm,
    WoltDriveIntegrationForm,
    BoltIntegrationForm,
    DeliveryHeroIntegrationForm,
    GlovoLaasIntegrationForm,
    GlovoIntegrationForm,
    IntegrationMenuPreviewModal,
    PlatformMenuImportLink,
    IntegrationMenuTopCategoriesField,
    IntegrationScheduleEditor,
    MarketplaceOrderSettings,
    PSwitch
  },
  props: {
    /** Shown under the sticky bar (e.g. integration key + id). */
    heading: {
      type: String,
      default: ''
    },
    busy: {
      type: Boolean,
      default: false
    },
    saveDisabled: {
      type: Boolean,
      default: false
    },
    fieldNames: {
      type: Array,
      required: true
    },
    formData: {
      type: Object,
      required: true
    },
    formText: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    booleanFields: {
      type: Array,
      default: () => []
    },
    jsonFields: {
      type: Array,
      default: () => []
    },
    jsonHint: {
      type: String,
      default: ''
    },
    fieldLabelFn: {
      type: Function,
      default: null
    },
    readOnlyFields: {
      type: Array,
      default: () => []
    },
    integrationType: {
      type: String,
      default: ''
    },
    locations: {
      type: Array,
      default: () => []
    },
    woltRequiredFields: {
      type: Array,
      default: () => []
    },
    woltValidationErrors: {
      type: Object,
      default: () => ({})
    },
    woltDriveRequiredFields: {
      type: Array,
      default: () => []
    },
    woltDriveValidationErrors: {
      type: Object,
      default: () => ({})
    },
    deliveryHeroRequiredFields: {
      type: Array,
      default: () => []
    },
    deliveryHeroCredentialFields: {
      type: Array,
      default: () => []
    },
    deliveryHeroValidationErrors: {
      type: Object,
      default: () => ({})
    },
    regeneratingDeliveryHeroKeys: {
      type: Boolean,
      default: false
    },
    boltRequiredFields: {
      type: Array,
      default: () => []
    },
    boltValidationErrors: {
      type: Object,
      default: () => ({})
    },
    glovoLaasRequiredFields: {
      type: Array,
      default: () => []
    },
    glovoLaasValidationErrors: {
      type: Object,
      default: () => ({})
    },
    glovoRequiredFields: {
      type: Array,
      default: () => []
    },
    glovoValidationErrors: {
      type: Object,
      default: () => ({})
    },
    canDelete: {
      type: Boolean,
      default: false
    },
    canValidate: {
      type: Boolean,
      default: false
    },
    validateDisabled: {
      type: Boolean,
      default: false
    },
    occupiedLocationIds: {
      type: Array,
      default: () => []
    },
    isCreateMode: {
      type: Boolean,
      default: false
    },
    webhookRegistered: {
      type: Boolean,
      default: false
    },
    topCategories: {
      type: Array,
      default: () => []
    },
    showFieldValidation: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      acceptOrdersStatus: false,
      actionBusy: false,
      glovoTransactionId: null,
      trackedGlovoIntegrationId: null,
      credentialsFlowMode: 'activate',
      activationSteps: {
        save: { status: 'pending', message: '' },
        test: { status: 'pending', message: '' },
        webhook: { status: 'pending', message: '' }
      },
      menuValidationMounts: [],
      menuPreviewVisible: false,
      menuPreviewBusy: false,
      menuPreviewImporting: false,
      menuPreviewLayout: 'wolt',
      menuPreviewCurrency: 'RON',
      menuPreviewWarnings: [],
      menuPreviewCategories: [],
      menuPreviewError: '',
      menuPreviewTitle: '',
      menuPreviewSubtitle: ''
    }
  },
  beforeDestroy () {
    this.destroyMenuValidationMounts()
  },
  computed: {
    isMarketplaceIntegration () { return this.isWoltIntegration || this.isBoltIntegration || this.isGlovoIntegration },
    isWoltIntegration () {
      return String(this.integrationType || '').toLowerCase() === 'wolt'
    },
    isWoltDriveIntegration () {
      return String(this.integrationType || '').toLowerCase() === 'wolt_drive'
    },
    isDeliveryHeroIntegration () {
      return String(this.integrationType || '').toLowerCase() === 'delivery_hero'
    },
    isBoltIntegration () {
      return String(this.integrationType || '').toLowerCase() === 'bolt'
    },
    isGlovoLaasIntegration () {
      return String(this.integrationType || '').toLowerCase() === 'glovo_laas'
    },
    isGlovoIntegration () {
      return String(this.integrationType || '').toLowerCase() === 'glovo'
    },
    showIntegrationOtherSettings () {
      return this.isGlovoIntegration || this.isWoltIntegration || this.isBoltIntegration
    },
    showMenuLocationOtherSetting () {
      return this.isGlovoIntegration || this.isWoltIntegration || this.isBoltIntegration
    },
    showIntegrationActions () {
      if (!this.formData || !this.formData.id || !this.formData.location_id) return false
      return this.isGlovoIntegration || this.isWoltIntegration || this.isBoltIntegration
    },
    showFormLoader () {
      return this.busy || this.actionBusy
    },
    showScheduleAction () {
      return this.showIntegrationActions && (this.isWoltIntegration || this.isBoltIntegration)
    },
    integrationRecordId () {
      if (!this.formData) return null
      const id = this.formData.id != null ? this.formData.id : this.formData._id
      return id != null && id !== '' ? id : null
    },
    showDeleteButton () {
      return this.canDelete && this.integrationRecordId != null
    },
    showValidateButton () {
      return this.canValidate &&
        this.integrationRecordId != null &&
        (this.isGlovoLaasIntegration || this.isWoltDriveIntegration || this.isDeliveryHeroIntegration)
    },
    showActivateButton () {
      return !this.isCreateMode &&
        this.integrationRecordId != null &&
        (this.isGlovoIntegration || this.isWoltIntegration || this.isBoltIntegration)
    },
    activateDisabled () {
      return this.saveDisabled
    },
    useActivateFlow () {
      return this.isCreateMode &&
        (this.isGlovoLaasIntegration || this.isWoltDriveIntegration)
    },
    isWebhookCapableIntegration () {
      return this.isGlovoLaasIntegration || this.isWoltDriveIntegration
    },
    showWebhookStatus () {
      return this.isWebhookCapableIntegration && !this.isCreateMode && this.integrationRecordId != null
    },
    shouldIncludeWebhookStep () {
      if (!this.isWebhookCapableIntegration) return false
      // During validation we only need the registration step when it has not been done yet.
      // During activation the webhook always has to be registered for the new integration.
      if (this.credentialsFlowMode === 'validate') {
        return !this.webhookRegistered
      }
      return true
    },
    saveButtonLabel () {
      return this.useActivateFlow ? this.$t('general.activate') : this.$t('general.save')
    },
    saveButtonTitle () {
      return this.useActivateFlow ? this.$t('general.activate') : this.$t('general.save')
    },
    glovoMenuExportsToday () {
      if (!this.formData || this.formData.menu_exports_today == null) return 0
      return this.formData.menu_exports_today
    },
    glovoLastMenuExport () {
      return this.formData && this.formData.last_menu_export ? this.formData.last_menu_export : null
    },
    glovoLastExportLabel () {
      const exportRow = this.glovoLastMenuExport
      if (!exportRow) {
        return this.$t('gestiune.integrationsGlovoNoExport')
      }
      const dateValue = this.resolveGlovoExportDateValue(exportRow)
      if (!dateValue) {
        return this.$t('gestiune.integrationsGlovoNoExport')
      }
      return this.formatDateTime(dateValue)
    },
    glovoLastExportStatus () {
      const exportRow = this.glovoLastMenuExport
      if (!exportRow) return ''
      if (exportRow.export_status) return exportRow.export_status
      if (exportRow.is_successful === null || exportRow.is_successful === undefined) {
        return this.$t('gestiune.integrationsGlovoExportPending')
      }
      return ''
    },
    glovoLastExportDetails () {
      const exportRow = this.glovoLastMenuExport
      const details = exportRow &&
        exportRow.provider_response &&
        exportRow.provider_response.details
      return Array.isArray(details) ? details : []
    },
    glovoExportStatusBadge () {
      const exportRow = this.glovoLastMenuExport
      if (!exportRow) return null
      if (exportRow.is_successful === true) {
        return { type: 'success', label: this.$t('gestiune.integrationsGlovoExportSuccess') }
      }
      if (exportRow.is_successful === false) {
        return { type: 'error', label: this.$t('gestiune.integrationsGlovoExportFailed') }
      }
      if (exportRow.export_status == null || exportRow.export_status === '') {
        return { type: 'pending', label: this.$t('gestiune.integrationsGlovoExportPending') }
      }
      return { type: 'pending', label: this.$t('gestiune.integrationsGlovoExportInProgress') }
    },
    canCheckGlovoExportStatus () {
      if (this.glovoTransactionId) return true
      const exportRow = this.glovoLastMenuExport
      return Boolean(exportRow && exportRow.transaction_id)
    }
  },
  watch: {
    formData: {
      deep: true,
      immediate: true,
      handler (data) {
        if (!data) return
        if (this.isGlovoIntegration) {
          if (this.trackedGlovoIntegrationId !== data.id) {
            this.trackedGlovoIntegrationId = data.id
            this.glovoTransactionId = null
          }
          this.acceptOrdersStatus = Boolean(data.store_active)
          const tx = data.last_menu_export && data.last_menu_export.transaction_id
          if (tx) {
            this.glovoTransactionId = tx
          }
        } else if (this.isWoltIntegration || this.isBoltIntegration) {
          this.acceptOrdersStatus = Boolean(data.active)
        }
      }
    }
  },
  methods: {
    onSubmit () {
      if (this.saveDisabled || this.actionBusy) return
      if (this.useActivateFlow) {
        this.startActivationFlow()
        return
      }
      this.$emit('save')
    },
    resetActivationSteps () {
      const steps = {}
      this.getActivationFlowConfig().steps.forEach(step => {
        steps[step.id] = { status: 'pending', message: '', validationGroups: [] }
      })
      this.activationSteps = steps
    },
    getGlovoActivateFlowConfig () {
      return {
        title: this.$t('gestiune.integrationsGlovoActivateTitle'),
        confirm: this.$t('gestiune.integrationsGlovoActivateConfirm'),
        stay: this.$t('gestiune.integrationsGlovoActivateProgressStay'),
        steps: [
          {
            id: 'save',
            label: this.isCreateMode
              ? this.$t('gestiune.integrationsGlovoActivateStepSave')
              : this.$t('gestiune.integrationsValidateStepSave')
          },
          { id: 'validateMenu', label: this.$t('gestiune.integrationsGlovoActivateStepValidateMenu') },
          { id: 'deactivateLocation', label: this.$t('gestiune.integrationsGlovoActivateStepDeactivateLocation') },
          { id: 'pushMenu', label: this.$t('gestiune.integrationsGlovoActivateStepPushMenu') },
          { id: 'checkExportStatus', label: this.$t('gestiune.integrationsGlovoActivateStepCheckExportStatus') },
          { id: 'activateLocation', label: this.$t('gestiune.integrationsGlovoActivateStepActivateLocation') }
        ]
      }
    },
    getWoltBoltActivateFlowConfig () {
      return {
        title: this.$t('gestiune.integrationsActivateTitle'),
        confirm: this.$t('gestiune.integrationsMenuActivateConfirm'),
        stay: this.$t('gestiune.integrationsActivateProgressStay'),
        steps: [
          {
            id: 'save',
            label: this.isCreateMode
              ? this.$t('gestiune.integrationsActivateStepSave')
              : this.$t('gestiune.integrationsValidateStepSave')
          },
          { id: 'validateMenu', label: this.$t('gestiune.integrationsMenuActivateStepValidateMenu') },
          { id: 'deactivateLocation', label: this.$t('gestiune.integrationsMenuActivateStepDeactivateLocation') },
          { id: 'pushMenu', label: this.$t('gestiune.integrationsMenuActivateStepPushMenu') },
          { id: 'activateLocation', label: this.$t('gestiune.integrationsMenuActivateStepActivateLocation') }
        ]
      }
    },
    getActivationFlowConfig () {
      if (this.credentialsFlowMode === 'activate' && this.isGlovoIntegration) {
        return this.getGlovoActivateFlowConfig()
      }
      if (this.credentialsFlowMode === 'activate' && (this.isWoltIntegration || this.isBoltIntegration)) {
        return this.getWoltBoltActivateFlowConfig()
      }
      return this.getCredentialsFlowConfig()
    },
    getCredentialsFlowConfig () {
      if (this.credentialsFlowMode === 'validate') {
        const steps = [
          { id: 'save', label: this.$t('gestiune.integrationsValidateStepSave') },
          { id: 'test', label: this.$t('gestiune.integrationsValidateStepTest') }
        ]
        if (this.shouldIncludeWebhookStep) {
          steps.push({ id: 'webhook', label: this.$t('gestiune.integrationsValidateStepWebhook') })
        }
        return {
          title: this.$t('gestiune.integrationsValidateTitle'),
          confirm: this.$t('gestiune.integrationsValidateConfirm'),
          stay: this.$t('gestiune.integrationsValidateProgressStay'),
          steps
        }
      }
      const steps = [
        { id: 'save', label: this.$t('gestiune.integrationsActivateStepSave') },
        { id: 'test', label: this.$t('gestiune.integrationsActivateStepTest') }
      ]
      if (this.shouldIncludeWebhookStep) {
        steps.push({ id: 'webhook', label: this.$t('gestiune.integrationsActivateStepWebhook') })
      }
      return {
        title: this.$t('gestiune.integrationsActivateTitle'),
        confirm: this.$t('gestiune.integrationsActivateConfirm'),
        stay: this.$t('gestiune.integrationsActivateProgressStay'),
        steps
      }
    },
    escapeHtml (value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    activationStepIconHtml (status) {
      if (status === 'running') {
        return '<span class="integration-activation-step__icon integration-activation-step__icon--running" aria-hidden="true"></span>'
      }
      if (status === 'success') {
        return '<span class="integration-activation-step__icon integration-activation-step__icon--success" aria-hidden="true">✓</span>'
      }
      if (status === 'error') {
        return '<span class="integration-activation-step__icon integration-activation-step__icon--error" aria-hidden="true">✕</span>'
      }
      return '<span class="integration-activation-step__icon integration-activation-step__icon--pending" aria-hidden="true"></span>'
    },
    buildActivationProgressHtml () {
      const config = this.getActivationFlowConfig()
      const rows = config.steps.map(step => {
        const state = this.activationSteps[step.id] || { status: 'pending', message: '', validationGroups: [] }
        const messageClass = state.status === 'running'
          ? 'integration-activation-step__progress'
          : 'integration-activation-step__error'
        let detailsHtml = ''
        if (state.message) {
          detailsHtml += `<div class="${messageClass}">${this.escapeHtml(state.message)}</div>`
        }
        if (state.validationGroups && state.validationGroups.length) {
          detailsHtml += `<div class="integration-activation-step__details" data-menu-validation-mount="${this.escapeHtml(step.id)}"></div>`
        }
        return `<li class="integration-activation-step integration-activation-step--${this.escapeHtml(state.status)}">` +
          `<div class="integration-activation-step__row">` +
          this.activationStepIconHtml(state.status) +
          `<span class="integration-activation-step__label">${this.escapeHtml(step.label)}</span>` +
          `</div>${detailsHtml}</li>`
      }).join('')
      return `<p class="integration-activation-stay">${this.escapeHtml(config.stay)}</p>` +
        `<ul class="integration-activation-steps">${rows}</ul>`
    },
    destroyMenuValidationMounts () {
      if (!Array.isArray(this.menuValidationMounts)) {
        this.menuValidationMounts = []
        return
      }
      this.menuValidationMounts.forEach(vm => {
        if (vm && typeof vm.$destroy === 'function') {
          vm.$destroy()
        }
      })
      this.menuValidationMounts = []
    },
    mountMenuValidationCollapse (mountEl, groups) {
      if (!mountEl || !Array.isArray(groups) || !groups.length) return null
      const Constructor = Vue.extend(IntegrationMenuValidationCollapse)
      const vm = new Constructor({
        parent: this,
        propsData: {
          groups,
          onEditEntry: (target) => this.openMenuValidationEntity(target)
        }
      })
      vm.$mount(mountEl)
      this.menuValidationMounts.push(vm)
      return vm
    },
    openMenuValidationEntity (target) {
      const path = getMenuValidationEntityEditPath(target)
      if (!path) return
      const resolved = this.$router.resolve(path)
      const href = resolved.href || `/#${path}`
      window.open(href, '_blank')
    },
    mountActivationValidationCollapses () {
      const container = document.querySelector('.integration-activation-swal .swal2-content')
      if (!container) return
      container.querySelectorAll('[data-menu-validation-mount]').forEach((mountEl) => {
        const stepId = mountEl.getAttribute('data-menu-validation-mount')
        const state = this.activationSteps[stepId]
        if (!state || !Array.isArray(state.validationGroups) || !state.validationGroups.length) return
        this.mountMenuValidationCollapse(mountEl, state.validationGroups)
      })
    },
    refreshActivationProgressModal () {
      this.destroyMenuValidationMounts()
      const container = document.querySelector('.integration-activation-swal .swal2-content')
      if (container) {
        container.innerHTML = this.buildActivationProgressHtml()
        this.$nextTick(() => this.mountActivationValidationCollapses())
      }
    },
    updateActivationStep (stepId, status, message, validationGroups) {
      if (!this.activationSteps[stepId]) return
      this.$set(this.activationSteps, stepId, {
        status: status || 'pending',
        message: message || '',
        validationGroups: Array.isArray(validationGroups) ? validationGroups : []
      })
      this.$nextTick(() => this.refreshActivationProgressModal())
    },
    openActivationProgressModal (resetSteps = true) {
      if (resetSteps) this.resetActivationSteps()
      swal({
        title: this.getActivationFlowConfig().title,
        html: this.buildActivationProgressHtml(),
        showConfirmButton: false,
        showCancelButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: 'integration-activation-swal',
        onOpen: () => {
          this.$nextTick(() => this.mountActivationValidationCollapses())
        }
      }).catch(() => {})
    },
    finishActivationModal (success, onClosed) {
      swal({
        title: this.getActivationFlowConfig().title,
        html: this.buildActivationProgressHtml(),
        showConfirmButton: true,
        confirmButtonText: this.$t('general.ok'),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: 'integration-activation-swal',
        onOpen: () => {
          this.$nextTick(() => this.mountActivationValidationCollapses())
        },
        onClose: () => {
          this.destroyMenuValidationMounts()
        }
      }).then((result) => {
        if (result && typeof onClosed === 'function') {
          onClosed({ success: Boolean(success) })
        }
      }).catch(() => {})
    },
    confirmMenuValidationOverride (response) {
      if (!canProceedWithMenuValidation(response, (key) => this.$t(key))) {
        return Promise.resolve(false)
      }
      const groups = extractMenuValidationGroups(response, (key) => this.$t(key))
      this.updateActivationStep('validateMenu', 'error',
        this.$t('gestiune.integrationsMenuValidationProceedAnywayHint'), groups)
      return swal({
        title: this.getActivationFlowConfig().title,
        html: this.buildActivationProgressHtml(),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('gestiune.integrationsMenuValidationProceedAnyway'),
        cancelButtonText: this.$t('general.cancel2'),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: 'integration-activation-swal',
        onOpen: () => this.$nextTick(() => this.mountActivationValidationCollapses()),
        onClose: () => this.destroyMenuValidationMounts()
      }).then((result) => {
        if (result) this.openActivationProgressModal(false)
        return Boolean(result)
      }).catch(() => false)
    },
    startCredentialsFlow (mode, eventName, finishedEventName) {
      this.credentialsFlowMode = mode
      const config = this.getActivationFlowConfig()
      swal({
        text: config.confirm,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.cancel2')
      }).then((result) => {
        if (!result) return
        this.openActivationProgressModal()
        this.$emit(eventName, {
          updateStep: this.updateActivationStep,
          confirmMenuValidationOverride: this.confirmMenuValidationOverride,
          finish: (success) => {
            this.finishActivationModal(success, (payload) => {
              this.$emit(finishedEventName, payload)
            })
          }
        })
      }).catch(() => {})
    },
    startActivationFlow () {
      if (this.activateDisabled || this.actionBusy) return
      this.startCredentialsFlow('activate', 'activate', 'activation-finished')
    },
    startValidationFlow () {
      if (this.validateDisabled || this.busy || this.disabled || this.actionBusy) return
      this.startCredentialsFlow('validate', 'validate', 'validation-finished')
    },
    onDeleteClick () {
      if (this.busy || this.disabled || this.actionBusy) return
      this.$emit('delete')
    },
    buildIntegrationActionPayload () {
      return {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        location_id: this.formData.location_id
      }
    },
    getApiResponseMessage (response) {
      return extractApiResponseMessage(response)
    },
    resolveGlovoExportDateValue (exportRow) {
      if (!exportRow || typeof exportRow !== 'object') return null
      const candidates = [exportRow.created_at, exportRow.last_status_update]
      for (let i = 0; i < candidates.length; i += 1) {
        const value = candidates[i]
        if (typeof value === 'string' && value.trim()) return value.trim()
      }
      return null
    },
    isExportTransactionNotFoundResponse (response) {
      const message = this.getApiResponseMessage(response).toLowerCase()
      return message.includes('transaction not found') ||
        message.includes('no export transaction')
    },
    isMenuNotFoundResponse (response) {
      return isMenuNotFoundResponse(response)
    },
    getIntegrationActionErrorMessage (response, action) {
      return resolveIntegrationActionErrorMessage(
        response,
        this.integrationType,
        action,
        (key, ...args) => this.$t(key, ...args)
      )
    },
    formatGlovoApiFeedback (response, action = 'generic') {
      if (!response) return this.$t('gestiune.integrationsActionError')
      const message = this.getIntegrationActionErrorMessage(response, action)
      if (message) return message
      return this.$t('gestiune.integrationsActionError')
    },
    extractGlovoTransactionId (response) {
      if (!response || !response.data) return null
      const data = response.data.data || response.data
      return data.transaction_id ||
        (data.provider_response && data.provider_response.transaction_id) ||
        null
    },
    showIntegrationActionSuccess (text) {
      swal({
        text: text || this.$t('bolt.sync'),
        type: 'success',
        showConfirmButton: false
      }).catch(() => {})
    },
    showIntegrationActionError (text) {
      swal({
        text: text || this.$t('bolt.syncError'),
        type: 'error',
        showConfirmButton: false
      }).catch(() => {})
    },
    formatGlovoDetailsText (value) {
      if (value == null || value === '') return ''
      if (typeof value === 'string') return value
      try {
        return JSON.stringify(value, null, 2)
      } catch (e) {
        return String(value)
      }
    },
    showGlovoValidationSwal (response, success, fromPreview = false) {
      const menuNotFound = !success && isMenuNotFoundResponse(response)
      const groups = menuNotFound
        ? []
        : extractMenuValidationGroups(response, (key) => this.$t(key))
      const summary = getMenuValidationSummary(
        response,
        success,
        (key) => this.$t(key),
        menuNotFound
      )
      const summaryHtml = summary
        ? `<p class="integration-glovo-swal__summary">${this.escapeHtml(summary)}</p>`
        : ''
      const mountId = `menu-validation-swal-mount-${Date.now()}`
      const canProceed = !success && canProceedWithMenuValidation(response, (key) => this.$t(key))
      const proceedId = `${mountId}-proceed`
      const proceedHtml = canProceed
        ? `<p>${this.escapeHtml(this.$t('gestiune.integrationsMenuValidationProceedAnywayHint'))}</p>` +
          `<button type="button" id="${proceedId}" class="btn btn-warning">` +
          `${this.escapeHtml(this.$t('gestiune.integrationsMenuValidationProceedAnyway'))}</button>`
        : ''
      let proceeded = false

      swal({
        title: success
          ? this.$t('gestiune.integrationsGlovoValidateSuccessTitle')
          : this.$t('gestiune.integrationsGlovoValidationFailedTitle'),
        html: `${summaryHtml}<div id="${mountId}"></div>${proceedHtml}`,
        type: success ? 'success' : 'error',
        showCancelButton: !success,
        confirmButtonText: success
          ? this.$t('general.ok')
          : this.$t('gestiune.integrationsMenuValidationTryAgain'),
        cancelButtonText: this.$t('general.ok'),
        allowOutsideClick: success,
        customClass: 'integration-glovo-swal',
        onOpen: () => {
          const mountEl = document.getElementById(mountId)
          if (mountEl && groups.length) {
            this.mountMenuValidationCollapse(mountEl, groups)
          }
          const proceedButton = canProceed && document.getElementById(proceedId)
          if (proceedButton) {
            proceedButton.addEventListener('click', () => {
              if (proceeded) return
              proceeded = true
              proceedButton.disabled = true
              swal.close()
              this.validateAndPushMenu(true, fromPreview)
            })
          }
        },
        onClose: () => {
          this.destroyMenuValidationMounts()
        }
      }).then((result) => {
        if (!success && result && !proceeded) {
          this.validateAndPushMenu(false, fromPreview)
        }
      }).catch(() => {})
    },
    refreshIntegrationData () {
      this.$emit('refresh')
    },
    buildGenericMenuValidatePayload () {
      const payload = this.buildIntegrationActionPayload()
      payload.integration = String(this.integrationType || '').toLowerCase()
      return payload
    },
    requestMenuValidation (callback) {
      if (this.isGlovoIntegration) {
        const payload = this.buildIntegrationActionPayload()
        payload.glovo_company_id = this.formData.id
        this.apiRequest('/glovo/menu/validate', 'POST', payload, callback)
        return
      }
      if (this.isWoltIntegration || this.isBoltIntegration) {
        this.apiRequest(
          '/integrations/menu/validate',
          'POST',
          this.buildGenericMenuValidatePayload(),
          callback
        )
        return
      }
      if (typeof callback === 'function') callback(null)
    },
    isMenuValidationPassed (response, fromPreview = false) {
      const menuNotFound = isMenuNotFoundResponse(response)
      if (menuNotFound) {
        this.showGlovoValidationSwal({
          status: false,
          errors: [{
            code: 'MENU_EMPTY',
            message: this.$t('gestiune.integrationsGlovoMenuEmpty')
          }]
        }, false, fromPreview)
        return false
      }
      const success = isMenuValidationSuccessful(
        response,
        (key) => this.$t(key),
        menuNotFound
      )
      if (!success) {
        this.showGlovoValidationSwal(response, false, fromPreview)
        return false
      }
      return true
    },
    openMenuPreview () {
      if (!this.showIntegrationActions || this.actionBusy || this.menuPreviewBusy) return

      this.menuPreviewVisible = true
      this.menuPreviewBusy = true
      this.menuPreviewError = ''
      this.menuPreviewWarnings = []
      this.menuPreviewCategories = []
      this.menuPreviewLayout = String(this.integrationType || 'wolt').toLowerCase()
      this.menuPreviewTitle = this.$t('gestiune.integrationsMenuPreviewTitle', {
        platform: String(this.integrationType || '').toUpperCase()
      })
      this.menuPreviewSubtitle = this.$t('gestiune.integrationsMenuPreviewSubtitle')

      const payload = this.buildIntegrationActionPayload()
      payload.integration = this.menuPreviewLayout
      payload.id = this.formData.id

      this.apiRequest('/integrations/menu/preview', 'POST', payload, (response) => {
        this.menuPreviewBusy = false
        if (response && response.status && response.data) {
          const data = response.data.data || response.data
          this.menuPreviewLayout = data.layout || this.menuPreviewLayout
          this.menuPreviewCurrency = data.currency || 'RON'
          this.menuPreviewWarnings = data.warnings || []
          this.menuPreviewCategories = (data.display && data.display.categories) || []
          if (!this.menuPreviewCategories.length) {
            this.menuPreviewError = this.$t('gestiune.integrationsGlovoMenuEmpty')
          }
        } else {
          const msg = this.getApiResponseMessage(response)
          this.menuPreviewError = msg || this.$t('gestiune.integrationsGlovoMenuEmpty')
        }
      })
    },
    closeMenuPreview () {
      if (this.menuPreviewBusy || this.menuPreviewImporting) return
      this.menuPreviewVisible = false
      this.menuPreviewError = ''
      this.menuPreviewWarnings = []
      this.menuPreviewCategories = []
    },
    confirmImportMenuFromPreview () {
      if (this.menuPreviewBusy || this.menuPreviewImporting || !this.menuPreviewCategories.length) return

      swal({
        title: this.$t('gestiune.integrationsMenuPreviewImportConfirmTitle'),
        text: this.$t('gestiune.integrationsMenuPreviewImportConfirmText'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('gestiune.integrationsMenuPreviewImport'),
        cancelButtonText: this.$t('general.cancel2')
      }).then((result) => {
        if (!result) return
        this.importMenuFromPreview()
      }).catch(() => {})
    },
    enrollWolt () {
      if (!this.isWoltIntegration || !this.showIntegrationActions || this.disabled || this.busy || this.actionBusy) return
      this.actionBusy = true
      const payload = this.buildIntegrationActionPayload()
      payload.return_to = 'integrations'
      this.apiRequest('/wolt/integrate-location/' + this.formData.id, 'POST', payload, (response) => {
        this.actionBusy = false
        if (response && response.status && response.data) {
          window.location.href = response.data
        } else {
          this.showIntegrationActionError(this.getIntegrationActionErrorMessage(response, 'enrollWolt'))
        }
      })
    },
    importMenuFromPreview () {
      this.validateAndPushMenu(false, true)
    },
    validateAndPushMenu (skipValidation = false, fromPreview = false) {
      if (!this.showIntegrationActions || this.disabled || this.actionBusy || this.menuPreviewBusy) return
      this.actionBusy = true
      if (fromPreview) this.menuPreviewImporting = true
      const finish = (pushResponse) => {
        this.actionBusy = false
        if (fromPreview) {
          this.menuPreviewImporting = false
          if (pushResponse && pushResponse.status) this.menuPreviewVisible = false
        }
      }
      const push = () => this.pushIntegrationMenu(finish)
      if (skipValidation === true) {
        push()
        return
      }
      this.requestMenuValidation((response) => {
        if (!this.isMenuValidationPassed(response, fromPreview)) {
          finish(null)
          return
        }
        push()
      })
    },
    pushIntegrationMenu (onComplete) {
      const payload = this.buildIntegrationActionPayload()
      let url = ''

      if (this.isGlovoIntegration) {
        url = '/glovo/menu/send-url'
        payload.glovo_company_id = this.formData.id
      } else if (this.isWoltIntegration) {
        url = '/wolt/export-menu'
        payload.wolt_company_id = this.formData.id
      } else if (this.isBoltIntegration) {
        url = '/bolt/push-menu'
        payload.bolt_company_id = this.formData.id
      } else {
        if (typeof onComplete === 'function') onComplete(null)
        return
      }

      this.apiRequest(url, 'POST', payload, (response) => {
        if (typeof onComplete === 'function') onComplete(response)
        if (response && response.status) {
          if (this.isGlovoIntegration) {
            const tx = this.extractGlovoTransactionId(response)
            if (tx) this.glovoTransactionId = tx
            const successText = tx
              ? `${this.$t('gestiune.integrationsGlovoPushAccepted')}\n${tx}`
              : this.$t('gestiune.integrationsGlovoPushAccepted')
            this.showIntegrationActionSuccess(successText)
            this.refreshIntegrationData()
          } else {
            this.showIntegrationActionSuccess()
          }
        } else {
          if (this.isMenuNotFoundResponse(response)) {
            this.showIntegrationActionError(this.$t('gestiune.integrationsGlovoMenuEmpty'))
          } else {
            const message = this.isGlovoIntegration
              ? this.formatGlovoApiFeedback(response, 'pushMenu')
              : this.getIntegrationActionErrorMessage(response, 'pushMenu')
            this.showIntegrationActionError(message || undefined)
          }
        }
      })
    },
    checkGlovoExportStatus () {
      if (!this.isGlovoIntegration || this.actionBusy || !this.canCheckGlovoExportStatus) return
      const payload = this.buildIntegrationActionPayload()
      payload.glovo_company_id = this.formData.id
      if (this.glovoTransactionId) {
        payload.transaction_id = this.glovoTransactionId
      }
      this.actionBusy = true
      this.apiRequest('/glovo/menu/export-status', 'POST', payload, (response) => {
        this.actionBusy = false
        if (response && response.status) {
          const tx = this.extractGlovoTransactionId(response)
          if (tx) this.glovoTransactionId = tx
          const data = response.data && (response.data.data || response.data)
          const statusText = data && data.export_status
            ? String(data.export_status)
            : this.$t('gestiune.integrationsGlovoStatusUpdated')
          this.showIntegrationActionSuccess(statusText)
          this.refreshIntegrationData()
        } else if (this.isExportTransactionNotFoundResponse(response)) {
          swal({
            text: this.$t('gestiune.integrationsGlovoNoExport'),
            type: 'info',
            confirmButtonText: this.$t('general.ok')
          }).catch(() => {})
        } else {
          this.showIntegrationActionError(this.formatGlovoApiFeedback(response, 'pushMenu'))
        }
      })
    },
    handleAcceptOrdersStatus () {
      if (!this.showIntegrationActions || this.actionBusy) return

      const payload = this.buildIntegrationActionPayload()
      payload.active = this.acceptOrdersStatus
      let url = ''

      if (this.isGlovoIntegration) {
        url = '/glovo/toggle-location-status'
        payload.glovo_company_id = this.formData.id
      } else if (this.isWoltIntegration) {
        url = '/wolt/toggle-location-status'
        payload.wolt_company_id = this.formData.id
      } else if (this.isBoltIntegration) {
        url = '/bolt/handle-open'
        payload.bolt_company_id = this.formData.id
      } else {
        return
      }

      this.actionBusy = true
      this.apiRequest(url, 'POST', payload, (response) => {
        this.actionBusy = false
        if (response && response.status) {
          if (this.isGlovoIntegration) {
            this.$set(this.formData, 'store_active', this.acceptOrdersStatus)
          } else {
            this.$set(this.formData, 'active', this.acceptOrdersStatus)
          }
          this.showIntegrationActionSuccess()
        } else {
          this.acceptOrdersStatus = !this.acceptOrdersStatus
          this.showIntegrationActionError(
            this.getIntegrationActionErrorMessage(response, 'toggleLocation')
          )
        }
      })
    },
    syncIntegrationSchedule () {
      if (!this.showScheduleAction || this.disabled || this.busy || this.actionBusy) return

      const payload = this.buildIntegrationActionPayload()
      payload.schedule = Array.isArray(this.formData.schedule)
        ? JSON.parse(JSON.stringify(this.formData.schedule))
        : []

      let url = ''
      if (this.isWoltIntegration) {
        url = '/wolt/update-schedule'
        payload.wolt_company_id = this.formData.id
      } else if (this.isBoltIntegration) {
        url = '/bolt/update-schedule'
        payload.bolt_company_id = this.formData.id
      } else {
        return
      }

      this.actionBusy = true
      this.apiRequest(url, 'POST', payload, (response) => {
        this.actionBusy = false
        if (response && response.status) {
          this.showIntegrationActionSuccess()
        } else {
          this.showIntegrationActionError(
            this.getIntegrationActionErrorMessage(response, 'syncSchedule')
          )
        }
      })
    }
  }
}
</script>

<style scoped>
.integration-form-shell {
  position: relative;
}
.integration-form-shell > .show-loader {
  z-index: 10001;
}
.integration-form-shell > .show-loader .el-loading-spinner {
  z-index: 10003;
}
.integration-form-shell > .show-loader:after {
  z-index: 10002;
}
/* Match Delete / Save / Validate top-bar actions: soft fill, no visible border,
   colored border on hover (same interaction for all three). */
.settings-top-bar .top-bar .btn.delete,
.settings-top-bar .top-bar .btn.validate,
.settings-top-bar .top-bar .btn.activate,
.settings-top-bar .top-bar .btn.save {
  width: auto;
  height: 40px;
  padding: 8px 18px !important;
  line-height: 22px;
  font-size: 14px;
  font-weight: 400;
  text-transform: none;
  border-radius: 50px;
  border-width: 1px;
  border-style: solid;
  box-sizing: border-box;
}
.settings-top-bar .top-bar .btn.delete {
  background: #FFEBEA;
  color: #FF0D19;
  border-color: transparent;
}
.settings-top-bar .top-bar .btn.delete:hover,
.settings-top-bar .top-bar .btn.delete:focus {
  background: #FFEBEA;
  color: #FF0D19;
  border-color: #FF0D19;
  opacity: 1;
}
.settings-top-bar .top-bar .btn.validate,
.settings-top-bar .top-bar .btn.activate {
  background: #E4F4FF;
  color: #0097FC;
  border-color: transparent;
}
.settings-top-bar .top-bar .btn.validate:hover,
.settings-top-bar .top-bar .btn.validate:focus,
.settings-top-bar .top-bar .btn.activate:hover,
.settings-top-bar .top-bar .btn.activate:focus {
  background: #E4F4FF;
  color: #0097FC;
  border-color: #0097FC;
}
.settings-top-bar .top-bar .btn.save {
  background: #EBF8FA;
  color: #17B086;
  border-color: transparent;
}
.settings-top-bar .top-bar .btn.save:hover,
.settings-top-bar .top-bar .btn.save:focus {
  background: #EBF8FA;
  color: #17B086;
  border-color: #17B086;
  opacity: 1;
}
.settings-top-bar .top-bar .btn.delete:disabled,
.settings-top-bar .top-bar .btn.delete.disabled,
.settings-top-bar .top-bar .btn.validate:disabled,
.settings-top-bar .top-bar .btn.activate:disabled,
.settings-top-bar .top-bar .btn.save:disabled {
  opacity: 0.55;
  pointer-events: none;
}
.integration-form-layout__card {
  height: 100%;
  width: 100%;
  margin-top: 15px;
}
.integration-form-actions-card {
  margin-bottom: 15px;
}
.integration-form-actions__row {
  align-items: center;
  margin-bottom: 20px;
}
.integration-form-actions__row:last-child {
  margin-bottom: 0;
}
.integration-form-actions__row label {
  margin: 0;
  line-height: 1.4;
}
.integration-form-actions__accept-orders {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.integration-form-actions__menu-hint {
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.4;
  color: #c62828;
}
.integration-form-actions__menu-hint:last-of-type {
  margin-bottom: 20px;
}
.integration-form-actions__menu-hint .fa-info-circle {
  margin-right: 6px;
  color: #c62828;
}
.integration-webhook-status {
  margin-top: 10px;
}
.integration-webhook-status__row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.integration-webhook-status__label {
  font-weight: 600;
  color: #25396F;
}
.integration-webhook-status__badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.6;
}
.integration-webhook-status__badge--ok {
  background-color: #e6f4ea;
  color: #2e7d32;
}
.integration-webhook-status__badge--warn {
  background-color: #fdecea;
  color: #c62828;
}
.integration-webhook-status__hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #b26a00;
}
.integration-glovo-menu {
  margin-bottom: 20px;
}
.integration-glovo-menu__summary {
  margin-bottom: 16px;
}
.integration-glovo-menu__summary-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.integration-glovo-menu__summary-label {
  font-weight: 600;
  color: #25396F;
  min-width: 120px;
}
.integration-glovo-menu__badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.6;
}
.integration-glovo-menu__badge--success {
  background-color: #e6f4ea;
  color: #2e7d32;
}
.integration-glovo-menu__badge--error {
  background-color: #fdecea;
  color: #c62828;
}
.integration-glovo-menu__badge--pending {
  background-color: #fff4e5;
  color: #b26a00;
}
.integration-glovo-menu__details {
  margin-top: 8px;
}
.integration-glovo-menu__detail-line {
  margin: 0 0 4px;
  font-size: 13px;
  color: #c62828;
}
.integration-glovo-menu__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
</style>

<style>
.integration-glovo-swal.swal2-popup {
  width: 40em;
  max-width: calc(100vw - 32px);
}
.integration-glovo-swal .swal2-content {
  text-align: left;
}
.integration-glovo-swal__summary {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
}
.integration-glovo-swal__section + .integration-glovo-swal__section {
  margin-top: 4px;
}
.integration-glovo-swal .integration-menu-validation-list {
  margin-top: 8px;
}
</style>

<style>
.integration-activation-swal.swal2-popup {
  width: 40em;
  max-width: calc(100vw - 32px);
}
.integration-activation-swal .swal2-content {
  text-align: left;
}
.integration-activation-stay {
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 600;
  color: #555;
}
.integration-activation-steps {
  list-style: none;
  margin: 0;
  padding: 0;
}
.integration-activation-step {
  margin-bottom: 12px;
}
.integration-activation-step:last-child {
  margin-bottom: 0;
}
.integration-activation-step__row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.integration-activation-step__label {
  font-size: 14px;
  line-height: 1.4;
}
.integration-activation-step__icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}
.integration-activation-step__icon--running {
  border: 2px solid #e0e0e0;
  border-top-color: #4caf50;
  border-radius: 50%;
  animation: integration-activation-spin 0.8s linear infinite;
}
.integration-activation-step__icon--success {
  color: #2e7d32;
}
.integration-activation-step__icon--error {
  color: #c62828;
}
.integration-activation-step__icon--pending {
  border: 2px solid #e0e0e0;
  border-radius: 50%;
}
.integration-activation-step__error {
  margin: 6px 0 0 28px;
  font-size: 12px;
  color: #c62828;
  line-height: 1.35;
  white-space: pre-line;
}
.integration-activation-step__progress {
  margin: 6px 0 0 28px;
  font-size: 12px;
  color: #555;
  line-height: 1.35;
  white-space: pre-line;
}
.integration-activation-step__details {
  margin: 8px 0 0 28px;
  width: calc(100% - 28px);
}
.integration-activation-swal .integration-menu-validation-list {
  margin-top: 4px;
}
@keyframes integration-activation-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
.marketplace-section-heading h6 { margin: 12px 0 18px; padding: 0 0 12px; border-bottom: 1px solid #dfe5f4; color: #25396f; font-size: 15px; font-weight: 600; text-transform: none; letter-spacing: normal; }
.marketplace-field-hint { margin: 7px 0 0; color: #72809d; font-size: 12px; line-height: 1.5; }
.marketplace-action-grid { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: 20px; margin-top: 20px; }
.marketplace-action-panel { background: #fff; border: 1px solid #dfe5f4; border-radius: 10px; padding: 22px; min-width: 0; }
.marketplace-action-heading { display: flex; gap: 14px; margin-bottom: 20px; align-items: flex-start; }
.marketplace-action-heading > i { padding: 11px; background: #eee9fc; color: #674bd2; border-radius: 8px; font-size: 18px; }
.marketplace-action-heading h6 { font-size: 15px; font-weight: 600; margin: 0 0 6px; text-transform: none; letter-spacing: normal; color: #25396f; }
.marketplace-action-heading p { font-size: 12px; color: #72809d; margin: 0; line-height: 1.6; }
.marketplace-action-stack { display: flex; flex-direction: column; gap: 20px; }
.marketplace-action-panel .btn { border-radius: 6px; font-size: 12px; text-transform: none; padding: 10px 16px; white-space: normal; line-height: 1.5; }
.marketplace-action-panel .integration-glovo-menu__buttons .btn:first-child, .marketplace-primary-action { background: #674bd2 !important; border-color: #674bd2 !important; color: #fff !important; }
.marketplace-import-action { margin-top: 16px; }
.marketplace-action-notice { display: flex; gap: 9px; padding: 12px; background: #fff8e8; color: #8b661d; border-radius: 6px; margin-top: 20px; }
.marketplace-action-notice i { margin-top: 3px; }
.marketplace-action-notice p { margin: 0; font-size: 12px; line-height: 1.6; }
.marketplace-action-notice p + p { margin-top: 6px; }
.marketplace-action-line { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 14px 0; border-top: 1px solid #edf0f7; font-size: 13px; }
.marketplace-action-line label, .marketplace-action-line .switch { margin: 0; }
@media (max-width: 991px) { .marketplace-action-grid { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 575px) { .marketplace-action-panel { padding: 16px; } .marketplace-action-line { flex-wrap: wrap; } }
</style>

<style>
.marketplace-menu-settings { margin-bottom: 18px; }
.marketplace-menu-settings__language { margin-top: 16px; margin-bottom: 0; max-width: 280px; }
.marketplace-schedule-panel, .marketplace-notifications-panel { margin-top: 20px; }
.marketplace-schedule-panel__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.marketplace-schedule-panel__header > .btn { flex-shrink: 0; }
.marketplace-notifications-panel textarea.form-control { min-height: 76px !important; height: 76px !important; resize: vertical; }
.marketplace-order-settings + .marketplace-action-line { margin-top: 20px; }
@media (max-width: 767px) { .marketplace-schedule-panel__header { flex-wrap: wrap; margin-bottom: 16px; } }
</style>

<style>
.marketplace-setup-panel + .marketplace-setup-panel { margin-top: 20px; }
.marketplace-setup-panel .row > div:last-child .form-group { margin-bottom: 0; }
.marketplace-settings .marketplace-field-hint { margin-top: 7px; color: #72809d; font-size: 12px; font-weight: 400; line-height: 1.6; }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Integrations/IntegrationForm.vue